/**
 * Language referral links.
 *
 * A marketing link can pre-select the estimator's language, so a visitor
 * arriving from a French campaign lands in French without touching the
 * switcher:
 *
 *   https://estimator.trusscore.com/fr-ca                  path prefix
 *   https://estimator.trusscore.com/fr-ca/estimate/<guid>  prefix + deep link
 *   https://estimator.trusscore.com/?lang=fr-CA            query parameter
 *
 * The tag is captured once, before React mounts, and the URL is rewritten in
 * place so the address bar shows the clean base URL and React Router never
 * sees a path it has no route for. The tag itself is NOT resolved here — that
 * needs the locale registry, which LanguageContext owns — so this module only
 * remembers what the URL asked for and lets the context match it (loosely:
 * `fr` or `fr-FR` still lands on fr-CA).
 *
 * A link is marketing's guess about the visitor, not the visitor's own choice,
 * so it is deliberately NOT stored the way a switcher click is. It lives in
 * sessionStorage: it wins for this browser session (surviving reloads and
 * in-app navigation, since the URL is clean by then), and is gone the next
 * time the visitor comes back on their own. Using the switcher discards it
 * outright — a click is a decision, a link was only a hint.
 *
 * Only a two-letter language with an optional two-letter region is recognised
 * as a path prefix, and it is stripped whether or not the registry knows it.
 * That is safe precisely because no route in this app is a two-letter segment
 * — keep it that way.
 */

const PATH_PREFIX = /^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i;

/** Query parameters accepted as a language hint, first match wins. */
const QUERY_KEYS = ['lang', 'locale'];

const STORAGE_KEY = 'trusscore_locale_hint';

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(tag: string | null): void {
  try {
    if (tag) window.sessionStorage.setItem(STORAGE_KEY, tag);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / quota — the hint still applies to this page load */
  }
}

let captured: string | null = null;

/**
 * Read a language tag from the landing URL and strip it. A tag on the URL
 * replaces any earlier hint this session; no tag leaves the earlier one alone.
 */
export function captureUrlLocale(): string | null {
  if (typeof window === 'undefined') return captured;
  try {
    const { pathname, search, hash } = window.location;
    let path = pathname;
    const params = new URLSearchParams(search);
    let tag: string | null = null;

    const m = PATH_PREFIX.exec(pathname);
    if (m) {
      tag = m[1];
      path = pathname.slice(m[0].length) || '/';
    }

    for (const key of QUERY_KEYS) {
      const value = params.get(key)?.trim();
      if (value && !tag) tag = value;
      if (params.has(key)) params.delete(key);
    }

    if (!tag) {
      captured = readStored();
      return captured;
    }
    captured = tag;
    writeStored(tag);

    const query = params.toString();
    const clean = `${path}${query ? `?${query}` : ''}${hash}`;
    if (clean !== `${pathname}${search}${hash}`) {
      window.history.replaceState(window.history.state, '', clean);
    }
  } catch {
    /* URL parsing or history unavailable — behave as if nothing was asked */
  }
  return captured;
}

/** The tag a referral link asked for this session, if any. */
export function urlLocaleHint(): string | null {
  return captured;
}

/** Forget the referral hint — the visitor has made a choice of their own. */
export function clearUrlLocaleHint(): void {
  captured = null;
  writeStored(null);
}
